import { useState } from "react";
import dayjs from "dayjs";

import { Typography, Grid, Box, Tooltip, useTheme } from "@mui/material";

import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { DefaultPaper, ToggleButtons } from "@aclymatepackages/atoms";
import { EmissionsPieChart } from "@aclymatepackages/modules";

import { formatDecimal, letterSBoolean } from "@aclymatepackages/formatters";

import TransactionHearerDisplay from "./TransactionHeaderDisplay";

import SlideLayout from "../../layouts/SlideLayout";
import TransactionDetailsFooterRow from "../../modules/TransactionDetailsFooterRow";
import TransactionDetailsBlock from "./TransactionDetailsBlock";
import IdDisplayChip from "../../atoms/IdDisplayChip";

import { buildFormattedTagsObjs } from "../../../helpers/components/tags";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import useDisplayMeasurementSystem from "../../../helpers/hooks/displayMeasurementSystem";
import { useCachedFirebaseCrud } from "../../../helpers/firebase";

const DEFAULT_EMPLOYEE_NAME = "Default Emissions";

const DetailsTitleBlock = ({ name: transactionName, tonsCo2e, subtitle }) => {
  const { carbonUnit, convertCarbonUnits } = useDisplayMeasurementSystem();

  return (
    <>
      <Typography variant="h6">
        {transactionName}: {formatDecimal(convertCarbonUnits(tonsCo2e))}{" "}
        {carbonUnit}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {subtitle}
      </Typography>
    </>
  );
};

const DefaultEmissionsSentence = ({ tonsCo2e, type }) => {
  const { carbonUnit, convertCarbonUnits } = useDisplayMeasurementSystem();

  const { employees: allEmployees } = useEmissionsContext();
  const unconfirmedEmployees = allEmployees.filter(
    ({ status }) => status === "unconfirmed"
  );

  const unconfirmedEmployeesCount = unconfirmedEmployees.length;

  return (
    <>
      There were{" "}
      <strong>
        {formatDecimal(convertCarbonUnits(tonsCo2e))} {carbonUnit}
      </strong>{" "}
      of default {type} emissions that were estimated for this{" "}
      {type === "commuting" ? `office this` : ""} month. This number represents
      the default emissions that we calculate for the{" "}
      <strong>
        {unconfirmedEmployeesCount} employee
        {letterSBoolean(unconfirmedEmployeesCount)}
      </strong>{" "}
      who {unconfirmedEmployeesCount === 1 ? "has" : "have"} not yet filled out
      their employee survey.
    </>
  );
};

const DetailsPieChart = ({
  electricCarbonTons,
  electricRenewablesReplacementTons,
  gasCarbonTons
}) => {
  const emissionsArray = [
    {
      name: "Electricity",
      tonsCo2e: electricCarbonTons,
      subcategory: "electricity"
    },
    { name: "Gas", tonsCo2e: gasCarbonTons, subcategory: "gas" },
    {
      name: "Renewable Energy",
      tonsCo2e: electricRenewablesReplacementTons,
      subcategory: "renewable-energy"
    }
  ];

  return (
    <EmissionsPieChart dataArray={emissionsArray} viewMode="subcategories" />
  );
};

const HomeOfficeTooltipContent = ({ tonsCo2e, name, ...otherProps }) => {
  if (name === DEFAULT_EMPLOYEE_NAME) {
    return (
      <Typography variant="body2" color="textPrimary">
        <DefaultEmissionsSentence tonsCo2e={tonsCo2e} type="home office" />
      </Typography>
    );
  }

  return (
    <Box style={{ width: "150px" }}>
      <Grid container direction="column" spacing={1}>
        <Grid item>
          <DetailsPieChart {...otherProps} />
        </Grid>
        <Grid item>
          <Typography variant="subtitle2" color="textPrimary" align="center">
            {name}'s Home Office Emissions
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const CommutingTooltipContent = ({
  tonsCo2e,
  name,
  oneWayDistanceMi,
  daysPerWk
}) => {
  const { carbonUnit, convertCarbonUnits } = useDisplayMeasurementSystem();

  const generateTooltipTitle = () => {
    if (name !== DEFAULT_EMPLOYEE_NAME) {
      return (
        <>
          {name} emitted{" "}
          <strong>{formatDecimal(convertCarbonUnits(tonsCo2e))}</strong>{" "}
          {carbonUnit} by commuting{" "}
          <strong>{formatDecimal(oneWayDistanceMi)}</strong> miles (one-way) to
          this office <strong>{daysPerWk}</strong> day
          {letterSBoolean(daysPerWk)} per week.
        </>
      );
    }

    return <DefaultEmissionsSentence tonsCo2e={tonsCo2e} type="commuting" />;
  };

  return (
    <Typography variant="body2" color="textPrimary">
      {generateTooltipTitle()}
    </Typography>
  );
};

const EmployeeDisplayRow = ({
  name,
  percentageOfTotal,
  tonsCo2e,
  maxPercentage,
  selectedView,
  subcategory,
  tooltipContent
}) => {
  const theme = useTheme();
  const { carbonUnit, convertCarbonUnits } = useDisplayMeasurementSystem();

  const isDefaultEmployee = name === DEFAULT_EMPLOYEE_NAME;
  const isSmallPercentage = percentageOfTotal / maxPercentage < 0.3;

  return (
    <Grid item container spacing={1} flexWrap="no-wrap" alignItems="center">
      <Grid item xs={4}>
        <Typography variant="subtitle2" align="right">
          {name}
        </Typography>
      </Grid>
      <Grid item xs={7}>
        <Box display="flex" columnGap={1} alignItems="center">
          <Tooltip
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "white",
                  boxShadow: theme.shadows[5]
                }
              }
            }}
            title={<Box p={1}>{tooltipContent}</Box>}
          >
            <Box
              borderRadius={2}
              p={isSmallPercentage ? 2 : 1}
              style={{
                backgroundColor: isDefaultEmployee
                  ? theme.palette.backgroundGray.dark
                  : theme.palette[subcategory].main,
                width: `${(percentageOfTotal / maxPercentage) * 100}%`
              }}
            >
              {!isSmallPercentage && (
                <Typography
                  display="block"
                  variant="caption"
                  align="center"
                  color={
                    isDefaultEmployee ? theme.palette.text.secondary : "#fff"
                  }
                >
                  {selectedView === "volume"
                    ? formatDecimal(convertCarbonUnits(tonsCo2e))
                    : formatDecimal(percentageOfTotal * 100)}
                  {selectedView === "volume" ? ` ${carbonUnit}` : "%"}
                </Typography>
              )}
            </Box>
          </Tooltip>
          {isSmallPercentage && (
            <Typography
              display="block"
              variant="caption"
              align="center"
              color="textPrimary"
            >
              {selectedView === "volume"
                ? formatDecimal(convertCarbonUnits(tonsCo2e))
                : formatDecimal(percentageOfTotal * 100)}
              {selectedView === "volume" ? ` ${carbonUnit}` : "%"}
            </Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

const EmployeesListLayout = ({
  displayArray,
  subcategory,
  TooltipContent,
  ...otherProps
}) => {
  const { palette } = useTheme();

  const { carbonUnit } = useDisplayMeasurementSystem();

  const [selectedView, setSelectedView] = useState("volume");

  const sortedDisplayArray = displayArray.sort(
    (a, b) => b.percentageOfTotal - a.percentageOfTotal
  );
  const [{ percentageOfTotal: maxPercentage }] = sortedDisplayArray;

  return (
    <Grid container spacing={2} column id="outer-container">
      <Grid item>
        <DetailsTitleBlock
          {...otherProps}
          subtitle={
            <>
              This graphic displays all of the employees who contributed to
              these emissions. Hover over any of the colored bars to view more
              details about that employee's{" "}
              {subcategory === "employee-commuting"
                ? "commuting"
                : "home office usage"}
              .
            </>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <DefaultPaper
          style={{
            backgroundColor: palette.backgroundGray.light,
            overflow: "auto"
          }}
        >
          <Grid container spacing={1} direction="column">
            <Grid item container justifyContent="center">
              <Grid item>
                <ToggleButtons
                  value={selectedView}
                  onChange={(val) => setSelectedView(val)}
                  buttons={[
                    { name: carbonUnit, value: "volume" },
                    { name: "%", value: "percentage" }
                  ]}
                />
              </Grid>
            </Grid>
            {displayArray.map((row, idx) => (
              <EmployeeDisplayRow
                key={idx}
                {...row}
                maxPercentage={maxPercentage}
                selectedView={selectedView}
                subcategory={subcategory}
                tooltipContent={<TooltipContent {...row} />}
              />
            ))}
          </Grid>
        </DefaultPaper>
      </Grid>
    </Grid>
  );
};

const CommutingDisplay = ({
  name: transactionName,
  tonsCo2e: transactionTonsCo2e,
  details,
  employeeName
}) => {
  const { defaultEmissionsTons, employees, commuteDetails } = details || {};

  const buildEmployeesArray = () => {
    const defaultEmissionsObj = defaultEmissionsTons
      ? [
          {
            name: DEFAULT_EMPLOYEE_NAME,
            tonsCo2e: defaultEmissionsTons,
            percentageOfTotal: defaultEmissionsTons / transactionTonsCo2e
          }
        ]
      : [];

    if (employees) {
      const employeesPercentages = employees.map((employee) => {
        const { tonsCo2e } = employee;
        const percentageOfTotal = tonsCo2e / transactionTonsCo2e || 0;
        return { ...employee, percentageOfTotal };
      });
      return [...employeesPercentages, ...defaultEmissionsObj];
    }

    const {
      tonsCo2e: employeeTonsCo2e,
      oneWayDistanceMi,
      daysPerWk
    } = commuteDetails || {};

    return [
      {
        name: employeeName,
        tonsCo2e: employeeTonsCo2e,
        oneWayDistanceMi,
        daysPerWk,
        percentageOfTotal: employeeTonsCo2e / transactionTonsCo2e
      },
      ...defaultEmissionsObj
    ];
  };

  return (
    <EmployeesListLayout
      name={transactionName}
      tonsCo2e={transactionTonsCo2e}
      displayArray={buildEmployeesArray()}
      subcategory="employee-commuting"
      TooltipContent={CommutingTooltipContent}
    />
  );
};

const HomeOfficeDisplay = ({
  name: transactionName,
  tonsCo2e: transactionTonsCo2e,
  details
}) => {
  const { offices, defaultEmissions } = details || {};
  const { employees: allEmployees = [] } = useEmissionsContext();
  const hasUnconfirmedEmployees = allEmployees.some(
    ({ status }) => status === "unconfirmed"
  );

  const buildEmployeesArray = () => {
    const {
      monthlyElectricCarbonTons,
      monthlyGasCarbonTons,
      totalMonthlyTons
    } = defaultEmissions || {};
    const totalDefaultEmissionsTons =
      (monthlyElectricCarbonTons || 0) + (monthlyGasCarbonTons || 0);

    const defaultEmissionsObj =
      hasUnconfirmedEmployees && totalMonthlyTons
        ? [
            {
              name: DEFAULT_EMPLOYEE_NAME,
              ...defaultEmissions,
              tonsCo2e: totalDefaultEmissionsTons,
              percentageOfTotal: totalDefaultEmissionsTons / transactionTonsCo2e
            }
          ]
        : [];

    const employeesArray = (offices || []).map((office) => {
      const { totalTons, employeeName } = office;
      return {
        name: employeeName,
        tonsCo2e: totalTons,
        percentageOfTotal: totalTons / transactionTonsCo2e,
        ...office
      };
    });

    return [...employeesArray, ...defaultEmissionsObj];
  };

  return (
    <EmployeesListLayout
      name={transactionName}
      tonsCo2e={transactionTonsCo2e}
      displayArray={buildEmployeesArray()}
      subcategory="home-office"
      TooltipContent={HomeOfficeTooltipContent}
    />
  );
};

const LeasedOfficeDisplay = ({ details, ...otherProps }) => {
  const { totalGasCf, totalKwh, gasCarbonTons, electricCarbonTons } =
    details || {};

  const gasCfString = gasCarbonTons ? (
    <strong>{formatDecimal(totalGasCf)} cubic feet of gas</strong>
  ) : (
    ""
  );

  const elecKwhString = electricCarbonTons ? (
    <strong>{formatDecimal(totalKwh)} kilowatt-hours of electricity</strong>
  ) : (
    ""
  );

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <DetailsTitleBlock
          {...otherProps}
          subtitle={
            <>
              Based on this office's square footage and location, we estimated
              that this office consumed {gasCfString}{" "}
              {gasCarbonTons && electricCarbonTons ? "and" : ""} {elecKwhString}{" "}
              this month.
            </>
          }
        />
      </Grid>
      <Grid item>
        <DetailsPieChart {...details} />
      </Grid>
    </Grid>
  );
};

const RecurringTransactionDetails = ({ transaction, setIsSlideOpen }) => {
  const { newCollectionDoc } = useCachedFirebaseCrud();

  const [editTransaction, setEditTransaction] = useState(transaction);
  const onEditTransaction = (field) => (value) =>
    editObjectData(setEditTransaction, field, value);

  const { subcategory, date, id } = transaction;

  const isUtilityTransaction = ["gas", "electricity"].includes(subcategory);

  const onTransactionSave = (transactionDetails) =>
    newCollectionDoc("transactions", {
      date: dayjs(date).toDate(),
      status: "confirmed",
      archived: false,
      ...buildFormattedTagsObjs(transaction),
      ...transaction,
      ...transactionDetails
    });

  if (isUtilityTransaction) {
    return (
      <TransactionDetailsBlock
        transaction={editTransaction}
        editTransaction={onEditTransaction}
        setIsSlideOpen={setIsSlideOpen}
        parentSubcategory={subcategory}
        saveTransaction={onTransactionSave}
      />
    );
  }

  const recurringSubcategoryDisplays = {
    "employee-commuting": <CommutingDisplay {...transaction} />,
    "home-office": <HomeOfficeDisplay {...transaction} />,
    "upstream-leased-assets": <LeasedOfficeDisplay {...transaction} />
  };

  return (
    <SlideLayout
      isSlideOpen
      setIsSlideOpen={setIsSlideOpen}
      header={
        <Grid container alignItems="center" spacing={2} wrap="nowrap">
          <Grid item>
            <TransactionHearerDisplay transactionSubcategory={subcategory} />
          </Grid>
          {id && (
            <Grid item>
              <IdDisplayChip id={id} type="emission" />
            </Grid>
          )}
        </Grid>
      }
      content={
        recurringSubcategoryDisplays[subcategory] || (
          <Typography variant="body1">
            No detailed view available for this transaction yet (we're working
            on it).
          </Typography>
        )
      }
      footer={
        isUtilityTransaction ? (
          <TransactionDetailsFooterRow transaction={transaction} />
        ) : (
          <Box />
        )
      }
    />
  );
};
export default RecurringTransactionDetails;
